/**
 * HTML renderer (Library / file-preview).
 *
 * Renders a `kind: "html"` artifact through the exact same
 * {@link HtmlPreviewFrame} the chat artifact canvas uses — a sandboxed
 * iframe with a Tailwind bootstrap + morphdom body patching — so opening
 * an HTML artifact from the Library is pixel-identical to opening it in
 * chat. It also inherits the frame's `forceScrollable` fix, so tall
 * dashboards that pin `html { overflow:hidden }` scroll here too.
 *
 * This path is read-only and static: the persisted body is resolved once
 * and handed to the frame, which applies it in a single morph. No
 * streaming, no style-prelude injection — the frame is the single source
 * of truth for how HTML artifacts render on every surface.
 *
 * The sandbox omits `allow-same-origin`, so untrusted HTML never reaches
 * the host cookie/DOM (enforced inside {@link HtmlPreviewFrame}).
 */
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { HtmlPreviewFrame } from "@/components/artifacts/html-preview-frame";

import { useFileText } from "../hooks/use-file-text";
import type { FileRendererProps } from "../types";

export function HtmlRenderer({ source }: FileRendererProps) {
  const state = useFileText(source);

  /**
   * The frame signals `onReady` after its bootstrap loads and paints the
   * first body morph. We hold a loader over it until then so the user never
   * sees the blank white iframe between mount and first paint. Reset when
   * the source changes so a re-open re-shows the loader.
   */
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
  }, [source.id]);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Unable to render HTML.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <HtmlPreviewFrame
        artifactId={source.id}
        html={state.text}
        title={source.filename || "HTML preview"}
        onReady={() => setReady(true)}
      />
      {!ready ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <Loader2 className="size-8 animate-spin text-primary" />
        </div>
      ) : null}
    </div>
  );
}
